import type { IncomingCall, SoftphoneStatus } from "./SoftphoneProvider";

export type CallTone = "active" | "ended" | "error";

export interface CallDisplay {
  // Primary status line on the card / pill (the live mm:ss timer for in-call).
  statusLine: string;
  // Secondary copy shown under the status line (error states only).
  detail?: string;
  tone: CallTone;
  // Avatar accent: blue while the call is live, grey once it has ended.
  isLive: boolean;
  isTerminal: boolean;
  showMute: boolean; // mute/unmute toggle (in-call only)
  showHangup: boolean; // end-call button (live call)
  showAnswer: boolean; // answer/decline pair (incoming ring, CRM-905)
  showKeypad: boolean; // DTMF dial pad (answered call only, CRM-1190)
}

function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Single source of truth for what the call card and the minimized pill render
 * for a given softphone status. Mirrors the Figma in-call states (CRM-811 +
 * the CRM-905 incoming ring): Incoming call / Ringing → mm:ss (with mute) →
 * No answer / Call ended · mm:ss / Can't reach.
 */
export function describeCall(
  status: SoftphoneStatus,
  opts: { elapsedSeconds: number; callDurationSeconds: number | null }
): CallDisplay {
  const base = {
    detail: undefined as string | undefined,
    showMute: false,
    showHangup: false,
    showAnswer: false,
    showKeypad: false
  };

  switch (status) {
    case "incoming":
      return {
        ...base,
        statusLine: "Incoming call",
        tone: "active",
        isLive: true,
        isTerminal: false,
        showAnswer: true
      };
    case "connecting":
      return {
        ...base,
        statusLine: "Ringing…",
        tone: "active",
        isLive: true,
        isTerminal: false,
        showHangup: true
      };
    case "in-call":
      return {
        ...base,
        statusLine: formatDuration(opts.elapsedSeconds),
        tone: "active",
        isLive: true,
        isTerminal: false,
        showMute: true,
        showHangup: true,
        showKeypad: true
      };
    case "reconnecting":
      return {
        ...base,
        statusLine: "Reconnecting…",
        tone: "active",
        isLive: true,
        isTerminal: false,
        showMute: true,
        showHangup: true,
        // The leg is still up (the SDK is re-establishing media), so keep the
        // pad reachable rather than yanking it out from under a menu prompt.
        showKeypad: true
      };
    case "ending":
      return {
        ...base,
        statusLine: "Ending…",
        tone: "active",
        isLive: true,
        isTerminal: false
      };
    case "no-answer":
      return {
        ...base,
        statusLine: "No answer",
        tone: "ended",
        isLive: false,
        isTerminal: true
      };
    case "call-ended":
      return {
        ...base,
        statusLine: `Call ended · ${formatDuration(opts.callDurationSeconds ?? 0)}`,
        tone: "ended",
        isLive: false,
        isTerminal: true
      };
    case "unreachable":
      return {
        ...base,
        statusLine: "Can't reach this number",
        detail:
          "Check the number and try again, or contact support if the issue persists.",
        tone: "error",
        isLive: false,
        isTerminal: true
      };
    // Device-level statuses, not call states — a card should not be on screen
    // for any of them. It still can be: a terminal card the user has not
    // dismissed yet when a re-dial fails, or a mic fault raised mid-call.
    // The old fallback claimed `isLive: true, isTerminal: false` with an empty
    // status line, which rendered a card with a blank 3rem gap, NO End-call
    // button (base hides every control) and an X wired to Minimize rather than
    // Dismiss — leaving no way to end a call that may still be connected.
    // Fail closed: say something true, and keep the card dismissable
    // (dismissCall hangs up the underlying leg).
    case "idle":
    case "registering":
    case "ready":
    case "error":
    case "mic-denied":
      return INTERRUPTED_DISPLAY;
    default:
      return interruptedFallback(status);
  }
}

// Exhaustiveness guard: `status` only narrows to `never` here while every
// SoftphoneStatus is handled above, so adding one without deciding what the
// card renders for it stops compiling.
function interruptedFallback(_status: never): CallDisplay {
  return INTERRUPTED_DISPLAY;
}

const INTERRUPTED_DISPLAY: CallDisplay = {
  detail: "Close this to return to the softphone.",
  showMute: false,
  showHangup: false,
  showAnswer: false,
  showKeypad: false,
  statusLine: "Call interrupted",
  tone: "error",
  isLive: false,
  isTerminal: true
};

/**
 * Who is ringing, named the way the call log names the call: the caller, then
 * the community their lead belongs to — "Eric Schultz · Roseville MHP LLC"
 * (CRM-1655). The dialed line ("To: …") is shown apart from it: it decides who
 * rings, not whose lead this is. Undefined when neither is known.
 */
export function incomingCallerLabel(
  call: Pick<IncomingCall, "leadName" | "leadProperty">
): string | undefined {
  const parts = [call.leadName?.trim(), call.leadProperty?.trim()].filter(
    Boolean
  );
  return parts.length > 0 ? parts.join(" · ") : undefined;
}
